import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Flag, Link, AlertTriangle, CheckCircle, Calendar, Plus, Trash2, Megaphone, Users, Shield, Star, Search, Send, BarChart2, MessageSquare, ArrowLeft, Bot } from 'lucide-react';
import './AdminPanel.css';
import useTelegramMainButton from '../hooks/useTelegramMainButton';

const AdminPanel = ({ user }) => {
    const [activeTab, setActiveTab] = useState('stats'); // subscription, ad, users, broadcast, staff, vip, moderation, stats, support, bot
    const [settings, setSettings] = useState({ forceSubscribe: false, requiredChannels: [], admins: [], vipStarPrice: 50, vipPerks: [], adEnabled: false, adText: '', adLink: '', welcomeMessage: '' });
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newAdminId, setNewAdminId] = useState('');
    const [botUsername, setBotUsername] = useState('');
    const [testResult, setTestResult] = useState(null); // { ok: bool, isAdmin: bool, error: string }
    const [testingChannel, setTestingChannel] = useState(false);
    const [toastMessage, setToastMessage] = useState(''); // إشعار حفظ تلقائي مرن

    // User Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Broadcasting State
    const [broadcastText, setBroadcastText] = useState('');
    const [broadcasting, setBroadcasting] = useState(false);

    // Ban Duration Modal State
    const [banModalOpen, setBanModalOpen] = useState(false);
    const [banTargetId, setBanTargetId] = useState(null);
    const [banType, setBanType] = useState('user'); // 'user' or 'report'
    const [banDuration, setBanDuration] = useState('permanent');

    // Stats & Support States
    const [stats, setStats] = useState(() => {
        try {
            const cached = localStorage.getItem('hams_cached_admin_stats');
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    });
    const [statsLoading, setStatsLoading] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [supportLoading, setSupportLoading] = useState(false);
    const [supportSubTab, setSupportSubTab] = useState('tickets'); // 'tickets', 'suggestions'
    const [replyModalOpen, setReplyModalOpen] = useState(false);
    const [replyTarget, setReplyTarget] = useState(null); // { type, id, text }
    const [replyText, setReplyText] = useState('');
    const [submittingReply, setSubmittingReply] = useState(false);

    const handleConfirmBan = async () => {
        if (!banTargetId) return;
        setBanModalOpen(false);
        if (banType === 'user') {
            await handleExecuteUserAction(banTargetId, 'ban', { duration: banDuration });
        } else if (banType === 'report') {
            await handleBanUser(banTargetId, banDuration);
        }
    };

    const handleSearchUsers = useCallback(async (query = searchQuery) => {
        setSearching(true);
        try {
            const res = await fetch(`/api/admin/users?query=${encodeURIComponent(query)}`, {
                headers: { 'x-user-id': user?.telegramId }
            });
            if (res.ok) {
                const data = await res.json();
                setSearchResults(data.users || []);
            } else {
                showToast('❌ فشل جلب المستخدمين');
            }
        } catch (err) {
            console.error("Search error:", err);
            showToast('⚠️ خطأ في الاتصال بالسيرفر');
        } finally {
            setSearching(false);
        }
    }, [searchQuery, user?.telegramId]);

    const handleExecuteUserAction = async (targetTelegramId, action, extraData = {}) => {
        let confirmMsg = '';
        if (action === 'ban') {
            // Confirmation is handled by the premium modal
        }
        else if (action === 'unban') confirmMsg = 'هل أنت متأكد من إلغاء حظر هذا المستخدم؟';
        else if (action === 'promote_vip') confirmMsg = 'هل أنت متأكد من ترقية هذا المستخدم إلى VIP لمدة شهر؟';
        else if (action === 'demote_vip') confirmMsg = 'هل أنت متأكد من إلغاء ترقية VIP لهذا المستخدم؟';
        else if (action === 'promote_admin') confirmMsg = 'هل أنت متأكد من ترقية هذا المستخدم إلى مشرف؟';
        else if (action === 'demote_admin') confirmMsg = 'هل أنت متأكد من إلغاء رتبة المشرف لهذا المستخدم؟';

        if (confirmMsg && !window.confirm(confirmMsg)) return;

        try {
            const res = await fetch('/api/admin/action', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': user.telegramId
                },
                body: JSON.stringify({ telegramId: targetTelegramId, action, extraData })
            });

            if (res.ok) {
                const data = await res.json();
                showToast('✨ تم تنفيذ الإجراء بنجاح');
                setSearchResults(prev => prev.map(u => u.telegramId.toString() === targetTelegramId.toString() ? { ...u, ...data.user } : u));
                if (action.includes('admin')) {
                    fetchSettings();
                }
            } else {
                const errData = await res.json();
                showToast(`❌ فشل الإجراء: ${errData.error || 'خطأ غير معروف'}`);
            }
        } catch (e) {
            console.error("Action error:", e);
            showToast('⚠️ خطأ في الاتصال بالسيرفر');
        }
    };

    const handleSendBroadcast = async () => {
        if (!broadcastText.trim()) {
            showToast('⚠️ يرجى كتابة نص المنشور أولاً');
            return;
        }

        if (!window.confirm('هل أنت متأكد من إرسال هذا المنشور كرسالة إذاعية لجميع مستخدمي البوت؟')) return;

        setBroadcasting(true);
        try {
            const res = await fetch('/api/admin/broadcast', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': user.telegramId
                },
                body: JSON.stringify({ message: broadcastText })
            });

            if (res.ok) {
                const data = await res.json();
                showToast(`✨ تم إطلاق الإذاعة بنجاح لـ ${data.totalUsers} مستخدم`);
                setBroadcastText('');
            } else {
                const errData = await res.json();
                showToast(`❌ فشل إرسال الإذاعة: ${errData.error || 'خطأ غير معروف'}`);
            }
        } catch (e) {
            console.error("Broadcast error:", e);
            showToast('⚠️ خطأ في الاتصال بالسيرفر');
        } finally {
            setBroadcasting(false);
        }
    };

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin/settings', {
                headers: { 'x-user-id': user?.telegramId }
            });
            if (res.ok) {
                const data = await res.json();
                setSettings({ 
                    ...data.settings, 
                    admins: data.settings.admins || [],
                    adEnabled: data.settings.adEnabled !== undefined ? data.settings.adEnabled : false,
                    adText: data.settings.adText || '',
                    adLink: data.settings.adLink || '',
                    welcomeMessage: data.settings.welcomeMessage || ''
                });
                setBotUsername(data.botUsername || '');
            }
        } catch (e) {
            console.error("Failed to fetch settings", e);
        } finally {
            setLoading(false);
        }
    }, [user?.telegramId]);

    const fetchReports = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin/reports', {
                headers: { 'x-user-id': user?.telegramId }
            });
            if (res.ok) {
                const data = await res.json();
                setReports(data.reports);
            }
        } catch (e) {
            console.error("Failed to fetch reports", e);
        } finally {
            setLoading(false);
        }
    }, [user?.telegramId]);


    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res = await fetch('/api/admin/stats', {
                headers: { 'x-user-id': user?.telegramId }
            });
            if (res.ok) {
                const data = await res.json();
                setStats(data);
                localStorage.setItem('hams_cached_admin_stats', JSON.stringify(data));
            } else {
                showToast('❌ فشل جلب الإحصائيات');
            }
        } catch (err) {
            console.error("Stats fetch error:", err);
            showToast('⚠️ خطأ في الاتصال بالسيرفر');
        } finally {
            setStatsLoading(false);
        }
    }, [user?.telegramId]);

    const fetchTickets = useCallback(async () => {
        setSupportLoading(true);
        try {
            const res = await fetch('/api/admin/support/tickets', {
                headers: { 'x-user-id': user?.telegramId }
            });
            if (res.ok) {
                const data = await res.json();
                setTickets(data);
            }
        } catch (err) {
            console.error("Tickets fetch error:", err);
        } finally {
            setSupportLoading(false);
        }
    }, [user?.telegramId]);

    const fetchSuggestions = useCallback(async () => {
        setSupportLoading(true);
        try {
            const res = await fetch('/api/admin/support/suggestions', {
                headers: { 'x-user-id': user?.telegramId }
            });
            if (res.ok) {
                const data = await res.json();
                setSuggestions(data);
            }
        } catch (err) {
            console.error("Suggestions fetch error:", err);
        } finally {
            setSupportLoading(false);
        }
    }, [user?.telegramId]);

    const handleSendReply = async () => {
        if (!replyText.trim() || !replyTarget) return;
        setSubmittingReply(true);
        try {
            const res = await fetch(`/api/admin/support/reply/${replyTarget.type}/${replyTarget.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': user.telegramId
                },
                body: JSON.stringify({ reply: replyText })
            });

            if (res.ok) {
                showToast('✨ تم إرسال الرد وإشعار المستخدم بنجاح');
                setReplyModalOpen(false);
                setReplyText('');
                setReplyTarget(null);
                if (replyTarget.type === 'ticket') {
                    fetchTickets();
                } else {
                    fetchSuggestions();
                }
            } else {
                showToast('❌ فشل إرسال الرد');
            }
        } catch (err) {
            console.error("Reply error:", err);
            showToast('⚠️ خطأ في الاتصال بالسيرفر');
        } finally {
            setSubmittingReply(false);
        }
    };

    useEffect(() => {
        if (!user || !user.isAdmin) return;
        if (activeTab === 'moderation') {
            fetchReports();
        } else if (activeTab === 'users') {
            handleSearchUsers('');
        } else if (activeTab === 'broadcast') {
            // No fetch needed on load
        } else if (activeTab === 'stats') {
            fetchStats();
        } else if (activeTab === 'support') {
            if (supportSubTab === 'tickets') {
                fetchTickets();
            } else {
                fetchSuggestions();
            }
        } else {
            fetchSettings();
        }
    }, [activeTab, supportSubTab, user, fetchSettings, fetchReports, handleSearchUsers, fetchStats, fetchTickets, fetchSuggestions]);

    const showToast = (message) => {
        setToastMessage(message);
        setTimeout(() => setToastMessage(''), 2500);
    };

    const saveSettingsToDb = async (updatedSettings) => {
        setSettings(updatedSettings);
        try {
            const res = await fetch('/api/admin/settings', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': user.telegramId
                },
                body: JSON.stringify(updatedSettings)
            });
            if (res.ok) {
                showToast('✨ تم حفظ التغييرات تلقائياً');
            } else {
                showToast('❌ عذراً، فشل حفظ التغييرات');
            }
        } catch (e) {
            console.error("Error auto-saving settings", e);
            showToast('⚠️ خطأ في الاتصال بالسيرفر');
        }
    };

    const handleSaveSettings = async () => {
        try {
            const res = await fetch('/api/admin/settings', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': user.telegramId
                },
                body: JSON.stringify(settings)
            });
            if (res.ok) {
                showToast('✨ تم حفظ الإعدادات بنجاح');
            } else {
                showToast('❌ حدث خطأ أثناء الحفظ');
            }
        } catch (e) {
            console.error("Error saving settings", e);
            showToast('⚠️ خطأ في الاتصال بالسيرفر');
        }
    };

    const isNativeMainActive = useTelegramMainButton({
        text: "حفظ الإعدادات",
        onClick: handleSaveSettings,
        isVisible: false, // لم نعد بحاجة لتعطيل الشاشة أو زر الحفظ اليدوي المزعج
        isEnabled: !loading
    });

    const handleUpdateReport = async (reportId, status) => {
        try {
            const res = await fetch(`/api/admin/reports/${reportId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': user.telegramId
                },
                body: JSON.stringify({ status })
            });
            if (res.ok) {
                setReports(reports.map(r => r._id === reportId ? { ...r, status } : r));
            }
        } catch (e) {
            console.error("Error updating report status", e);
        }
    };

    const handleBanUser = async (telegramId, duration = 'permanent') => {
        try {
            const res = await fetch('/api/admin/ban', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': user.telegramId
                },
                body: JSON.stringify({ telegramId, duration })
            });
            if (res.ok) {
                showToast('✨ تم حظر المستخدم بنجاح');
            } else {
                const errData = await res.json();
                showToast(`❌ فشل الحظر: ${errData.error || 'خطأ غير معروف'}`);
            }
        } catch (e) {
            console.error("Error banning user", e);
            showToast('⚠️ خطأ في الاتصال بالسيرفر');
        }
    };


    const handleTestChannel = async (channelId) => {
        setTestingChannel(true);
        setTestResult(null);
        try {
            const res = await fetch('/api/admin/test-channel', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': user.telegramId
                },
                body: JSON.stringify({ channelIdentifier: channelId })
            });
            const data = await res.json();
            setTestResult(data);
        } catch (e) {
            setTestResult({ ok: false, error: 'خطأ في الاتصال بالسيرفر' });
        } finally {
            setTestingChannel(false);
        }
    };

    const handleAddBotAsAdmin = () => {
        if (!botUsername) {
            alert('لم يتم العثور على معرف البوت. يرجى المحاولة لاحقاً.');
            return;
        }
        const deepLink = `https://t.me/${botUsername}?startchannel=true&admin=post_messages+edit_messages+delete_messages+invite_users+manage_chat+promote_members`;
        window.Telegram?.WebApp?.openTelegramLink(deepLink);
    };

    if (!user || !user.isAdmin) {
        return (
            <div className="admin-main-container flex-center">
                <div className="empty-state">
                    <Shield size={64} className="text-danger" color="#f43f5e" />
                    <h2 className="empty-title">غير مصرح لك</h2>
                    <p className="empty-subtitle">هذه الصفحة مخصصة لمديري النظام فقط.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-main-container">
            <div className="admin-header">
                <Shield className="header-icon" size={28} />
                <h2 className="page-title">لوحة تحكم الإدارة</h2>
            </div>

            <div className="admin-sub-tabs">
                <button
                    className={`sub-tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stats')}
                >
                    <BarChart2 size={18} /> الإحصائيات الشاملة 📊
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'support' ? 'active' : ''}`}
                    onClick={() => setActiveTab('support')}
                >
                    <MessageSquare size={18} /> الدعم والاقتراحات 📬
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'subscription' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subscription')}
                >
                    <Link size={18} /> الاشتراك
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'ad' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ad')}
                >
                    <Megaphone size={18} /> إدارة الإعلان
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'bot' ? 'active' : ''}`}
                    onClick={() => setActiveTab('bot')}
                >
                    <Bot size={18} /> إعدادات البوت 🤖
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    <Users size={18} /> إدارة الأعضاء
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'broadcast' ? 'active' : ''}`}
                    onClick={() => setActiveTab('broadcast')}
                >
                    <Send size={18} /> الإذاعة العامة
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'staff' ? 'active' : ''}`}
                    onClick={() => setActiveTab('staff')}
                >
                    <Users size={18} /> فريق العمل
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'vip' ? 'active' : ''}`}
                    onClick={() => setActiveTab('vip')}
                >
                    <Star size={18} color="#eab308" /> الـ VIP
                </button>
                <button
                    className={`sub-tab-btn ${activeTab === 'moderation' ? 'active' : ''}`}
                    onClick={() => setActiveTab('moderation')}
                >
                    <Shield size={18} /> البلاغات
                </button>
            </div>

            {loading ? (
                <div className="skeleton-container mt-4">
                    <div className="skeleton-item" style={{ height: '120px' }}></div>
                    <div className="skeleton-item" style={{ height: '120px' }}></div>
                </div>
            ) : (
                <div className="admin-content-section animate-fade">
                    {activeTab === 'stats' && (
                        <div className="stats-dashboard animate-fade">
                            {statsLoading && stats && (
                                <div className="stats-revalidating-loader animate-pulse" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 16px',
                                    background: 'rgba(255, 255, 255, 0.03)',
                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                    borderRadius: '12px',
                                    fontSize: '0.85rem',
                                    color: 'var(--text-secondary, #888)',
                                    marginBottom: '16px',
                                    width: 'fit-content'
                                }}>
                                    <span className="animate-spin" style={{
                                        display: 'inline-block',
                                        width: '12px',
                                        height: '12px',
                                        border: '2px solid rgba(255,255,255,0.2)',
                                        borderTopColor: 'var(--primary-color, #38bdf8)',
                                        borderRadius: '50%'
                                    }}></span>
                                    <span>جاري تحديث البيانات...</span>
                                </div>
                            )}
                            {!stats ? (
                                <div className="skeleton-container mt-4">
                                    <div className="skeleton-item" style={{ height: '140px' }}></div>
                                    <div className="skeleton-item" style={{ height: '240px' }}></div>
                                </div>
                            ) : (
                                <>
                                    {/* بطاقات الإحصائيات الفخمة */}
                                    <div className="stats-cards-grid">
                                        <div className="stats-card-premium">
                                            <div className="stats-card-icon-wrapper blue">
                                                <Users size={24} />
                                            </div>
                                            <div className="stats-card-details">
                                                <span className="stats-num">{stats.totalUsers}</span>
                                                <span className="stats-title">إجمالي المستخدمين</span>
                                            </div>
                                        </div>

                                        <div className="stats-card-premium">
                                            <div className="stats-card-icon-wrapper gold">
                                                <Star size={24} fill="currentColor" />
                                            </div>
                                            <div className="stats-card-details">
                                                <span className="stats-num">{stats.totalVip}</span>
                                                <span className="stats-title">أعضاء VIP نشطين</span>
                                            </div>
                                        </div>

                                        <div className="stats-card-premium">
                                            <div className="stats-card-icon-wrapper green">
                                                <Megaphone size={24} />
                                            </div>
                                            <div className="stats-card-details">
                                                <span className="stats-num">{stats.totalPrivateMessages}</span>
                                                <span className="stats-title">الرسائل المتبادلة</span>
                                            </div>
                                        </div>

                                        <div className="stats-card-premium">
                                            <div className="stats-card-icon-wrapper red">
                                                <AlertTriangle size={24} />
                                            </div>
                                            <div className="stats-card-details">
                                                <span className="stats-num">{stats.totalBanned}</span>
                                                <span className="stats-title">المستخدمين المحظورين</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* الرسوم البيانية */}
                                    <div className="stats-charts-row">
                                        {/* نسب الجنسين - Progress Rings */}
                                        <div className="stats-chart-card glassmorphic-panel">
                                            <h4>توزيع المستخدمين حسب الجنس 👥</h4>
                                            <div className="gender-progress-rings">
                                                {/* ذكر */}
                                                <div className="gender-ring-wrapper">
                                                    <div className="svg-container">
                                                        <svg width="120" height="120" viewBox="0 0 120 120">
                                                            <circle cx="60" cy="60" r="50" className="circle-bg" />
                                                            <circle 
                                                                cx="60" 
                                                                cy="60" 
                                                                r="50" 
                                                                className="circle-progress male" 
                                                                style={{ 
                                                                    strokeDasharray: 314, 
                                                                    strokeDashoffset: 314 - (314 * (stats.totalUsers > 0 ? (stats.gender.male / stats.totalUsers) : 0))
                                                                }} 
                                                            />
                                                        </svg>
                                                        <span className="ring-percent">
                                                            {stats.totalUsers > 0 ? Math.round((stats.gender.male / stats.totalUsers) * 100) : 0}%
                                                        </span>
                                                    </div>
                                                    <span className="ring-label male">ذكور ({stats.gender.male})</span>
                                                </div>

                                                {/* أنثى */}
                                                <div className="gender-ring-wrapper">
                                                    <div className="svg-container">
                                                        <svg width="120" height="120" viewBox="0 0 120 120">
                                                            <circle cx="60" cy="60" r="50" className="circle-bg" />
                                                            <circle 
                                                                cx="60" 
                                                                cy="60" 
                                                                r="50" 
                                                                className="circle-progress female" 
                                                                style={{ 
                                                                    strokeDasharray: 314, 
                                                                    strokeDashoffset: 314 - (314 * (stats.totalUsers > 0 ? (stats.gender.female / stats.totalUsers) : 0))
                                                                }} 
                                                            />
                                                        </svg>
                                                        <span className="ring-percent">
                                                            {stats.totalUsers > 0 ? Math.round((stats.gender.female / stats.totalUsers) * 100) : 0}%
                                                        </span>
                                                    </div>
                                                    <span className="ring-label female">إناث ({stats.gender.female})</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* التوزيع الجغرافي - Vertical SVG Bars */}
                                        <div className="stats-chart-card glassmorphic-panel">
                                            <h4>أكثر الدول تفاعلاً 🌍</h4>
                                            {stats.countries && stats.countries.length > 0 ? (
                                                <div className="countries-bar-chart">
                                                    {stats.countries.slice(0, 6).map((c, i) => {
                                                        const maxCount = Math.max(...stats.countries.map(co => co.count));
                                                        const percentHeight = maxCount > 0 ? (c.count / maxCount) * 80 : 0;
                                                        return (
                                                            <div key={i} className="bar-wrapper">
                                                                <div className="bar-value">{c.count}</div>
                                                                <div className="bar-container">
                                                                    <div 
                                                                        className="bar-fill" 
                                                                        style={{ height: `${percentHeight}%`, transition: 'height 1s ease' }} 
                                                                    />
                                                                </div>
                                                                <div className="bar-label" title={c.country}>{c.country || 'أخرى'}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="empty-state-mini">
                                                    لا توجد بيانات جغرافية متاحة حالياً
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'support' && (
                        <div className="support-dashboard animate-fade">
                            {/* شريط تبويبات الدعم الداخلي */}
                            <div className="support-sub-tabs mb-4">
                                <button 
                                    className={`support-tab-btn ${supportSubTab === 'tickets' ? 'active' : ''}`}
                                    onClick={() => setSupportSubTab('tickets')}
                                >
                                    <ShieldCheck size={16} /> تذاكر الدعم الفني ({tickets.length})
                                </button>
                                <button 
                                    className={`support-tab-btn ${supportSubTab === 'suggestions' ? 'active' : ''}`}
                                    onClick={() => setSupportSubTab('suggestions')}
                                >
                                    <Star size={16} /> اقتراحات وميزات ({suggestions.length})
                                </button>
                            </div>

                            {supportLoading ? (
                                <div className="skeleton-container mt-4">
                                    <div className="skeleton-item" style={{ height: '100px' }}></div>
                                    <div className="skeleton-item" style={{ height: '100px' }}></div>
                                </div>
                            ) : (
                                <div className="support-items-list">
                                    {supportSubTab === 'tickets' ? (
                                        tickets.length === 0 ? (
                                            <div className="empty-state">
                                                <ShieldCheck size={48} className="text-emerald-500 mb-2" />
                                                <p className="empty-title">لا توجد تذاكر دعم</p>
                                                <p className="empty-subtitle">جميع المستخدمين سعداء ولا توجد مشاكل معلقة! ✨</p>
                                            </div>
                                        ) : (
                                            tickets.map(ticket => (
                                                <div key={ticket._id} className={`support-item-card status-${ticket.status}`}>
                                                    <div className="support-item-header">
                                                        <div className="user-profile-meta">
                                                            <div className="user-avatar-wrapper">
                                                                {ticket.userAvatar ? (
                                                                    <img src={ticket.userAvatar} alt={ticket.userName} className="user-avatar-img" />
                                                                ) : (
                                                                    <div className="user-avatar-fallback">{(ticket.userName || 'م').charAt(0)}</div>
                                                                )}
                                                            </div>
                                                            <div className="user-text-details">
                                                                <span className="user-name">{ticket.userName}</span>
                                                                <span className="user-tg-id">ID: {ticket.userId}</span>
                                                            </div>
                                                        </div>
                                                        <div className="item-badge-status">
                                                            <span className={`status-badge-pill ${ticket.status}`}>
                                                                {ticket.status === 'replied' ? '✅ تم الرد' : ticket.status === 'closed' ? '🔒 مغلقة' : '⏳ قيد الانتظار'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="support-item-content">
                                                         <div className="ticket-messages-thread" style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(0, 0, 0, 0.2)', padding: '12px', borderRadius: '8px', margin: '8px 0' }}>
                                                             {(ticket.messages || [{ sender: 'user', text: ticket.message, createdAt: ticket.createdAt }]).map((msg, idx) => (
                                                                 <div key={idx} style={{ 
                                                                     alignSelf: msg.sender === 'user' ? 'flex-start' : 'flex-end', 
                                                                     background: msg.sender === 'user' ? 'rgba(255,255,255,0.05)' : 'rgba(59, 130, 246, 0.2)',
                                                                     padding: '8px 12px',
                                                                     borderRadius: '8px',
                                                                     fontSize: '0.82rem',
                                                                     maxWidth: '85%',
                                                                     textAlign: 'right'
                                                                 }}>
                                                                     <span style={{ fontWeight: 'bold', display: 'block', fontSize: '0.72rem', color: msg.sender === 'user' ? '#94a3b8' : '#60a5fa', marginBottom: '2px' }}>
                                                                         {msg.sender === 'user' ? '👤 المستخدم' : '✍️ الإدارة'}
                                                                     </span>
                                                                     {msg.text}
                                                                 </div>
                                                             ))}
                                                         </div>
                                                         {ticket.status !== 'closed' && (
                                                             <div className="support-item-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                                                 <button 
                                                                     className="btn-reply-support"
                                                                     onClick={() => {
                                                                         setReplyTarget({ type: 'ticket', id: ticket._id, text: ticket.message });
                                                                         setReplyText('');
                                                                         setReplyModalOpen(true);
                                                                     }}
                                                                 >
                                                                     <Send size={14} /> الرد على التذكرة
                                                                 </button>
                                                                 <button
                                                                     onClick={async () => {
                                                                         if (window.confirm('هل أنت متأكد من إغلاق هذه التذكرة؟')) {
                                                                             const res = await fetch(`/api/support/ticket/${ticket._id}/status`, {
                                                                                 method: 'POST',
                                                                                 headers: {
                                                                                     'Content-Type': 'application/json',
                                                                                     'x-user-id': user?.telegramId || ''
                                                                                 },
                                                                                 body: JSON.stringify({ status: 'closed' })
                                                                             });
                                                                             if (res.ok) {
                                                                                 showToast('🔒 تم إغلاق التذكرة بنجاح');
                                                                                 fetchTickets();
                                                                             } else {
                                                                                 showToast('❌ فشل إغلاق التذكرة');
                                                                             }
                                                                         }
                                                                     }}
                                                                     style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                                 >
                                                                     إغلاق التذكرة
                                                                 </button>
                                                             </div>
                                                         )}
                                                     </div>
                                                     
                                                </div>
                                            ))
                                        )
                                    ) : (
                                        suggestions.length === 0 ? (
                                            <div className="empty-state">
                                                <Star size={48} className="text-amber-500 mb-2" />
                                                <p className="empty-title">لا توجد اقتراحات</p>
                                                <p className="empty-subtitle">لم يتم تقديم اقتراحات جديدة من الأعضاء بعد.</p>
                                            </div>
                                        ) : (
                                            suggestions.map(suggestion => (
                                                <div key={suggestion._id} className={`support-item-card status-${suggestion.status}`}>
                                                    <div className="support-item-header">
                                                        <div className="user-profile-meta">
                                                            <div className="user-avatar-wrapper">
                                                                {suggestion.userAvatar ? (
                                                                    <img src={suggestion.userAvatar} alt={suggestion.userName} className="user-avatar-img" />
                                                                ) : (
                                                                    <div className="user-avatar-fallback">{(suggestion.userName || 'م').charAt(0)}</div>
                                                                )}
                                                            </div>
                                                            <div className="user-text-details">
                                                                <span className="user-name">{suggestion.userName}</span>
                                                                <span className="user-tg-id">ID: {suggestion.userId}</span>
                                                            </div>
                                                        </div>
                                                        <div className="item-badge-status">
                                                            <span className={`status-badge-pill ${suggestion.status}`}>
                                                                {suggestion.status === 'replied' ? '✅ تم المراجعة' : '⏳ قيد المراجعة'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="support-item-content">
                                                        <p className="message-body">{suggestion.suggestion}</p>
                                                        {suggestion.status === 'replied' && (
                                                            <div className="admin-reply-box">
                                                                <div className="reply-header">✍️ رد الإدارة:</div>
                                                                <p className="reply-body">{suggestion.reply}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {suggestion.status === 'pending' && (
                                                        <div className="support-item-actions">
                                                            <button 
                                                                className="btn-reply-support"
                                                                onClick={() => {
                                                                    setReplyTarget({ type: 'suggestion', id: suggestion._id, text: suggestion.suggestion });
                                                                    setReplyText('');
                                                                    setReplyModalOpen(true);
                                                                }}
                                                            >
                                                                <Send size={14} /> الرد على الاقتراح
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'subscription' && (
                        <div className="admin-card">
                            <div className="card-header border-b mb-4">
                                <Megaphone className="header-icon" size={20} />
                                <h3>إعدادات الاشتراك الإجباري</h3>
                            </div>

                            <div className="setting-item-flex mb-4">
                                <div className="setting-info">
                                    <label>تفعيل النظام</label>
                                    <span className="setting-desc">إجبار المستخدمين على الانضمام للقناة قبل الاستخدام.</span>
                                </div>
                                <label className="modern-switch">
                                    <input
                                        type="checkbox"
                                        checked={settings.forceSubscribe}
                                        onChange={(e) => {
                                            const updated = { ...settings, forceSubscribe: e.target.checked };
                                            saveSettingsToDb(updated);
                                        }}
                                    />
                                    <span className="modern-slider"></span>
                                </label>
                            </div>

                            <div className="setting-item-block">
                                <div className="flex-between mb-2">
                                    <label>القنوات المطلوبة</label>
                                    <button
                                        className="text-primary flex-center gap-1"
                                        style={{ fontSize: '0.8rem', background: 'none', border: 'none', cursor: 'pointer' }}
                                        onClick={handleAddBotAsAdmin}
                                    >
                                        <Link size={14} /> إضافة البوت لقناة
                                    </button>
                                </div>
                                <div className="moderators-list mb-4">
                                    {settings.requiredChannels.map((ch, idx) => (
                                        <div key={idx} className="moderator-item-card">
                                            <div className="mod-details">
                                                <span className="mod-name" style={{ direction: 'ltr' }}>{ch}</span>
                                            </div>
                                            <div className="mod-meta-actions">
                                                <button 
                                                    className="btn-secondary-admin" 
                                                    style={{ width: 'auto', padding: '6px 10px' }} 
                                                    disabled={testingChannel} 
                                                    onClick={() => handleTestChannel(ch)}
                                                >
                                                    {testingChannel ? 'جاري الفحص...' : 'فحص'}
                                                </button>
                                                <button className="btn-delete-mod" onClick={() => {
                                                    const updated = { ...settings, requiredChannels: settings.requiredChannels.filter(c => c !== ch) };
                                                    saveSettingsToDb(updated);
                                                }}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex-center" style={{ gap: '8px' }}>
                                    <input
                                        type="text"
                                        id="newChannelInput"
                                        className="admin-input"
                                        placeholder="@channel_username"
                                        style={{ flex: 1 }}
                                    />
                                    <button className="btn-save-admin" style={{ width: 'auto', padding: '12px' }} onClick={() => {
                                        const val = document.getElementById('newChannelInput').value.trim();
                                        if (val) {
                                            if (settings.requiredChannels.includes(val)) {
                                                showToast('⚠️ القناة مضافة بالفعل');
                                                return;
                                            }
                                            const updated = { ...settings, requiredChannels: [...settings.requiredChannels, val] };
                                            saveSettingsToDb(updated);
                                            document.getElementById('newChannelInput').value = '';
                                        }
                                    }}>
                                        <Plus size={20} />
                                    </button>
                                </div>

                                {testResult && (
                                    <div className={testResult.isAdmin ? "status-success-alert" : "status-error-alert"}>
                                        <div className="flex-center gap-2">
                                            {testResult.isAdmin ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                                            <span>
                                                {testResult.isAdmin
                                                    ? "البوت مشرف في هذه القناة ويعمل بشكل صحيح!"
                                                    : `مشكلة: ${testResult.error || "البوت ليس مشرفاً في هذه القناة"}`}
                                            </span>
                                        </div>
                                        {!testResult.isAdmin && (
                                            <button
                                                className="btn-save-admin mt-2"
                                                style={{ backgroundColor: '#f43f5e', fontSize: '0.85rem', padding: '8px' }}
                                                onClick={handleAddBotAsAdmin}
                                            >
                                                إضافة البوت كمشرف الآن
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'staff' && (
                        <>
                            <div className="admin-card mb-4">
                                <div className="card-header border-b mb-4">
                                    <Plus className="header-icon" size={20} />
                                    <h3>إضافة مشرف جديد</h3>
                                </div>
                                <div className="flex-center" style={{ gap: '8px' }}>
                                    <input
                                        type="text"
                                        value={newAdminId}
                                        onChange={(e) => setNewAdminId(e.target.value)}
                                        className="admin-input"
                                        placeholder="Telegram User ID"
                                        style={{ flex: 1 }}
                                    />
                                    <button className="btn-save-admin" style={{ width: 'auto', padding: '12px' }} onClick={() => {
                                        const val = newAdminId.trim();
                                        if (val) {
                                            if (settings.admins.includes(val)) {
                                                showToast('⚠️ المشرف مضاف بالفعل');
                                                return;
                                            }
                                            const updated = { ...settings, admins: [...settings.admins, val] };
                                            saveSettingsToDb(updated);
                                            setNewAdminId('');
                                        }
                                    }}>
                                        <Plus size={20} />
                                    </button>
                                </div>
                            </div>

                            <div className="admin-card">
                                <div className="card-header border-b mb-4">
                                    <Users className="header-icon" size={20} />
                                    <h3>فريق العمل الحالي</h3>
                                </div>
                                <div className="moderators-list">
                                    {settings.admins.map((adminId) => (
                                        <div key={adminId} className="moderator-item-card">
                                            <div className="mod-details">
                                                <span className="mod-name">مشرف</span>
                                                <span className="mod-id">ID: {adminId}</span>
                                            </div>
                                            <div className="mod-meta-actions">
                                                <button className="btn-delete-mod" onClick={() => {
                                                    if (!window.confirm(`هل أنت متأكد من إزالة المشرف ${adminId}؟`)) return;
                                                    const updated = { ...settings, admins: settings.admins.filter(a => a !== adminId) };
                                                    saveSettingsToDb(updated);
                                                }}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'vip' && (
                        <div className="admin-card animate-fade">
                            <div className="card-header border-b mb-4">
                                <Star className="header-icon" size={20} color="#eab308" />
                                <h3>إعدادات اشتراك VIP (نجوم تيليجرام)</h3>
                            </div>

                            <div className="setting-item-block mb-4">
                                <label>سعر الترقية بالنجوم (Telegram Stars)</label>
                                <div className="flex-center" style={{ gap: '8px' }}>
                                    <input
                                        type="number"
                                        value={settings.vipStarPrice || ''}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value, 10) || 0;
                                            const updated = { ...settings, vipStarPrice: val };
                                            saveSettingsToDb(updated);
                                        }}
                                        className="admin-input"
                                        placeholder="مثال: 50"
                                        style={{ flex: 1 }}
                                    />
                                    <span style={{ fontSize: '1rem', color: '#eab308', fontWeight: 'bold', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}><Star size={16} className="text-yellow-500 fill-yellow-500" /> نجمة</span>
                                </div>
                                <span className="setting-desc" style={{ fontSize: '0.78rem', color: '#64748b', display: 'block', marginTop: '6px' }}>
                                    يتم دفع هذا المبلغ بعملة النجوم داخل تطبيق تيليجرام وتفعيل اشتراك الـ VIP للمستخدم شهرياً فور نجاح الدفع.
                                </span>
                            </div>

                            <div className="setting-item-block">
                                <label>المزايا والميزات المدفوعة</label>
                                <div className="moderators-list mb-4">
                                    {(settings.vipPerks || []).map((perk, idx) => (
                                        <div key={idx} className="moderator-item-card">
                                            <div className="mod-details">
                                                <span className="mod-name">{perk}</span>
                                            </div>
                                            <div className="mod-meta-actions">
                                                <button className="btn-delete-mod" onClick={() => {
                                                    const updatedPerks = settings.vipPerks.filter((_, i) => i !== idx);
                                                    const updated = { ...settings, vipPerks: updatedPerks };
                                                    saveSettingsToDb(updated);
                                                }}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex-center" style={{ gap: '8px' }}>
                                    <input
                                        type="text"
                                        id="newPerkInput"
                                        className="admin-input"
                                        placeholder="مثال: مطابقات غير محدودة بدون فترات انتظار 🚀"
                                        style={{ flex: 1 }}
                                    />
                                    <button className="btn-save-admin" style={{ width: 'auto', padding: '12px' }} onClick={() => {
                                        const val = document.getElementById('newPerkInput').value.trim();
                                        if (val) {
                                            const updatedPerks = [...(settings.vipPerks || []), val];
                                            const updated = { ...settings, vipPerks: updatedPerks };
                                            saveSettingsToDb(updated);
                                            document.getElementById('newPerkInput').value = '';
                                        }
                                    }}>
                                        <Plus size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'ad' && (
                        <div className="admin-card animate-fade">
                            <div className="card-header border-b mb-4">
                                <Megaphone className="header-icon" size={20} />
                                <h3>إدارة الإعلانات والمساحات الترويجية</h3>
                            </div>

                            <div className="setting-item-flex mb-4">
                                <div className="setting-info">
                                    <label>تفعيل الشريط الإعلاني</label>
                                    <span className="setting-desc">تفعيل شريط الإعلان أعلى كافة الصفحات للمستخدمين غير المشتركين بـ VIP.</span>
                                </div>
                                <label className="modern-switch">
                                    <input
                                        type="checkbox"
                                        checked={settings.adEnabled || false}
                                        onChange={(e) => {
                                            const updated = { ...settings, adEnabled: e.target.checked };
                                            saveSettingsToDb(updated);
                                        }}
                                    />
                                    <span className="modern-slider"></span>
                                </label>
                            </div>

                            <div className="setting-item-block mb-4">
                                <label>نص الإعلان</label>
                                <textarea
                                    className="admin-input"
                                    rows="3"
                                    value={settings.adText || ''}
                                    placeholder="🎉 اكتب نص الإعلان الجذاب هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, adText: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, adText: e.target.value.trim() };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                                />
                                <span className="setting-desc">يتم حفظ النص تلقائياً بمجرد إلغاء التركيز أو الانتقال لحقل آخر (onBlur).</span>
                            </div>

                            <div className="setting-item-block">
                                <label>رابط الإعلان (اختياري)</label>
                                <input
                                    type="text"
                                    value={settings.adLink || ''}
                                    onChange={(e) => {
                                        setSettings({ ...settings, adLink: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, adLink: e.target.value.trim() };
                                        saveSettingsToDb(updated);
                                    }}
                                    className="admin-input"
                                    placeholder="مثال: https://t.me/channel"
                                />
                                <span className="setting-desc">الرابط الموجه للمستخدم عند نقره على الإعلان. يتم حفظه تلقائياً.</span>
                            </div>
                        </div>
                    )}

                    {activeTab === 'bot' && (
                        <div className="admin-card animate-fade">
                            <div className="card-header border-b mb-4">
                                <Bot className="header-icon" size={20} />
                                <h3>إعدادات رسائل وتكوين البوت 🤖</h3>
                            </div>

                            <div className="setting-item-block mb-6">
                                <label>رسالة الترحيب والبداية (/start)</label>
                                <span className="setting-desc" style={{ marginBottom: '8px', display: 'block' }}>
                                    يمكنك استخدام تنسيق HTML مثل <code>&lt;b&gt;نص غامق&lt;/b&gt;</code> و <code>&lt;i&gt;مائل&lt;/i&gt;</code> ورموز الإيموجي المميزة <code>&lt;tg-emoji emoji-id="..."&gt;😎&lt;/tg-emoji&gt;</code>.
                                </span>
                                <textarea
                                    className="admin-input"
                                    rows="10"
                                    value={settings.welcomeMessage || ''}
                                    placeholder="اكتب رسالة الترحيب والبداية للبوت هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, welcomeMessage: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, welcomeMessage: e.target.value };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', direction: 'rtl', fontSize: '0.9rem' }}
                                />
                                <span className="setting-desc">يتم حفظ الرسالة تلقائياً بمجرد إلغاء التركيز أو الانتقال لحقل آخر (onBlur).</span>
                            </div>

                            <div className="setting-item-block mb-6">
                                <label>كليشه القائمة الرئيسية</label>
                                <span className="setting-desc" style={{ marginBottom: '8px', display: 'block' }}>
                                    المتغيرات المدعومة: <code>{"{name}"}</code> (اسم المستخدم)، <code>{"{badge}"}</code> (شارة VIP إن وجدت).
                                </span>
                                <textarea
                                    className="admin-input"
                                    rows="8"
                                    value={settings.txtMainMenu || ''}
                                    placeholder="اكتب كليشه القائمة الرئيسية هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, txtMainMenu: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, txtMainMenu: e.target.value };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', direction: 'rtl', fontSize: '0.9rem' }}
                                />
                                <span className="setting-desc">تعديل رسالة القائمة الرئيسية للبوت. يتم حفظ النص تلقائياً.</span>
                            </div>

                            <div className="setting-item-block mb-6">
                                <label>كليشه المطابقة العشوائية (شاشة اختيار نوع المطابقة)</label>
                                <span className="setting-desc" style={{ marginBottom: '8px', display: 'block' }}>
                                    لا تدعم متغيرات خاصة.
                                </span>
                                <textarea
                                    className="admin-input"
                                    rows="8"
                                    value={settings.txtMatchWelcome || ''}
                                    placeholder="اكتب كليشه المطابقة العشوائية هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, txtMatchWelcome: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, txtMatchWelcome: e.target.value };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', direction: 'rtl', fontSize: '0.9rem' }}
                                />
                                <span className="setting-desc">تعديل شاشة الترحيب بالمطابقة. يتم حفظ النص تلقائياً.</span>
                            </div>

                            <div className="setting-item-block mb-6">
                                <label>كليشه جاري البحث (شاشة قائمة الانتظار)</label>
                                <span className="setting-desc" style={{ marginBottom: '8px', display: 'block' }}>
                                    لا تدعم متغيرات خاصة.
                                </span>
                                <textarea
                                    className="admin-input"
                                    rows="6"
                                    value={settings.txtMatchWaiting || ''}
                                    placeholder="اكتب كليشه جاري البحث هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, txtMatchWaiting: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, txtMatchWaiting: e.target.value };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', direction: 'rtl', fontSize: '0.9rem' }}
                                />
                                <span className="setting-desc">تعديل شاشة انتظار المطابقة. يتم حفظ النص تلقائياً.</span>
                            </div>

                            <div className="setting-item-block mb-6">
                                <label>كليشه تم العثور (عند بدء المحادثة المجهولة)</label>
                                <span className="setting-desc" style={{ marginBottom: '8px', display: 'block' }}>
                                    المتغيرات المدعومة: <code>{"{likes}"}</code> (عدد الإعجابات للطرف الآخر)، <code>{"{dislikes}"}</code> (عدد عدم الإعجابات للطرف الآخر).
                                </span>
                                <textarea
                                    className="admin-input"
                                    rows="8"
                                    value={settings.txtMatchFound || ''}
                                    placeholder="اكتب كليشه تم العثور هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, txtMatchFound: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, txtMatchFound: e.target.value };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', direction: 'rtl', fontSize: '0.9rem' }}
                                />
                                <span className="setting-desc">تعديل رسالة العثور على شريك. يتم حفظ النص تلقائياً.</span>
                            </div>

                            <div className="setting-item-block mb-6">
                                <label>كليشه قائمة أصدقائك</label>
                                <span className="setting-desc" style={{ marginBottom: '8px', display: 'block' }}>
                                    المتغيرات المدعومة: <code>{"{count}"}</code> (عدد الأصدقاء).
                                </span>
                                <textarea
                                    className="admin-input"
                                    rows="6"
                                    value={settings.txtFriendsList || ''}
                                    placeholder="اكتب كليشه قائمة أصدقائك هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, txtFriendsList: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, txtFriendsList: e.target.value };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', direction: 'rtl', fontSize: '0.9rem' }}
                                />
                                <span className="setting-desc">تعديل رسالة قائمة الأصدقاء. يتم حفظ النص تلقائياً.</span>
                            </div>

                            <div className="setting-item-block mb-6">
                                <label>كليشه معلومات الصديق</label>
                                <span className="setting-desc" style={{ marginBottom: '8px', display: 'block' }}>
                                    المتغيرات المدعومة: <code>{"{name}"}</code> (الاسم)، <code>{"{badge}"}</code> (شارة VIP)، <code>{"{level}"}</code> (المستوى)، <code>{"{xp}"}</code> (نقاط الخبرة).
                                </span>
                                <textarea
                                    className="admin-input"
                                    rows="8"
                                    value={settings.txtFriendInfo || ''}
                                    placeholder="اكتب كليشه معلومات الصديق هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, txtFriendInfo: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, txtFriendInfo: e.target.value };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', direction: 'rtl', fontSize: '0.9rem' }}
                                />
                                <span className="setting-desc">تعديل شاشة معلومات صديق محدد. يتم حفظ النص تلقائياً.</span>
                            </div>

                            <div className="setting-item-block mb-6">
                                <label>كليشه ملفك الشخصي</label>
                                <span className="setting-desc" style={{ marginBottom: '8px', display: 'block' }}>
                                    المتغيرات المدعومة: <code>{"{nickname}"}</code>، <code>{"{username}"}</code>، <code>{"{bio}"}</code>، <code>{"{gender}"}</code>، <code>{"{level}"}</code>، <code>{"{xp}"}</code>، <code>{"{friendsCount}"}</code>، <code>{"{vipStatus}"}</code>، <code>{"{privacy}"}</code>.
                                </span>
                                <textarea
                                    className="admin-input"
                                    rows="12"
                                    value={settings.txtUserProfile || ''}
                                    placeholder="اكتب كليشه ملفك الشخصي هنا..."
                                    onChange={(e) => {
                                        setSettings({ ...settings, txtUserProfile: e.target.value });
                                    }}
                                    onBlur={(e) => {
                                        const updated = { ...settings, txtUserProfile: e.target.value };
                                        saveSettingsToDb(updated);
                                    }}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', direction: 'rtl', fontSize: '0.9rem' }}
                                />
                                <span className="setting-desc">تعديل شاشة الملف الشخصي للمستخدم. يتم حفظ النص تلقائياً.</span>
                            </div>
                        </div>
                    )}

                    {activeTab === 'moderation' && (
                        <div className="reports-list">


                            {reports.length === 0 ? (
                                <div className="empty-state mt-4">
                                    <CheckCircle size={48} className="text-emerald-500 mb-2" />
                                    <p className="empty-title">النظام نظيف</p>
                                    <p className="empty-subtitle">لا توجد بلاغات مسجلة حالياً.</p>
                                </div>
                            ) : (
                                reports.map(report => (
                                    <div key={report._id} className={`report-card status-${report.status}`}>
                                        <div className="report-card-header">
                                            <div className="report-reason-badge">
                                                <Flag size={14} /> {report.reason}
                                            </div>
                                            <div className="report-date">
                                                <Calendar size={14} /> {new Date(report.createdAt).toLocaleDateString('ar-EG')}
                                            </div>
                                        </div>
                                        <div className="report-info-grid">
                                            <div className="info-cell">
                                                <span className="info-label">المُبلَّغ عنه (ID)</span>
                                                <span className="info-value" style={{ color: '#ef4444', fontWeight: 'bold', fontFamily: 'monospace' }}>{report.reportedEntityId}</span>
                                            </div>
                                            <div className="info-cell">
                                                <span className="info-label">المُبلِّغ (ID)</span>
                                                <span className="info-value" style={{ fontFamily: 'monospace', opacity: 0.7 }}>{report.reporterId}</span>
                                            </div>
                                            <div className="info-cell">
                                                <span className="info-label">البلاغات الفريدة</span>
                                                <span className="info-value" style={{ color: '#f59e0b', fontWeight: 'bold' }}>{report.uniqueReportsCount || 1}</span>
                                            </div>
                                            <div className="info-cell">
                                                <span className="info-label">الحالة</span>
                                                <span className={`status-text ${report.status}`}>{report.status}</span>
                                            </div>
                                        </div>
                                        <div className="report-actions">
                                            <button className="btn-action resolve" onClick={() => handleUpdateReport(report._id, 'resolved')}>حل</button>
                                            <button className="btn-action ignore" onClick={() => handleUpdateReport(report._id, 'ignored')}>تجاهل</button>
                                            <button 
                                                className="btn-action ban" 
                                                onClick={() => {
                                                    setBanTargetId(report.reportedEntityId);
                                                    setBanType('report');
                                                    setBanDuration('permanent');
                                                    setBanModalOpen(true);
                                                }}
                                            >
                                                حظر
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div className="admin-card animate-fade">
                            <div className="card-header border-b mb-4">
                                <Users className="header-icon" size={20} />
                                <h3>إدارة وبحث المستخدمين</h3>
                            </div>

                            <div className="search-bar-container">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                                    className="admin-input"
                                    placeholder="ابحث بالاسم، المعرّف (Username) أو الآيدي (Telegram ID)..."
                                    style={{ flex: 1 }}
                                />
                                <button 
                                    className="search-btn" 
                                    onClick={() => handleSearchUsers()}
                                    disabled={searching}
                                >
                                    {searching ? 'جاري البحث...' : <Search size={18} />}
                                </button>
                            </div>

                            {searching ? (
                                <div className="skeleton-container mt-4">
                                    <div className="skeleton-item" style={{ height: '80px' }}></div>
                                    <div className="skeleton-item" style={{ height: '80px' }}></div>
                                </div>
                            ) : (
                                <div className="users-list mt-4">
                                    {searchResults.length === 0 ? (
                                        <div className="empty-state">
                                            <Users size={48} className="text-muted mb-2" />
                                            <p className="empty-title">لا توجد نتائج</p>
                                            <p className="empty-subtitle">يرجى كتابة كلمة بحث أخرى أو التأكد من معرف المستخدم.</p>
                                        </div>
                                    ) : (
                                        searchResults.map(u => (
                                            <div key={u.telegramId} className="user-admin-card">
                                                <div className="user-profile-row">
                                                    <div className="user-avatar-wrapper">
                                                        {u.avatarUrl ? (
                                                            <img 
                                                                src={u.avatarUrl} 
                                                                alt={u.firstName} 
                                                                className="user-avatar-img"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div 
                                                            className="user-avatar-fallback" 
                                                            style={{ display: u.avatarUrl ? 'none' : 'flex' }}
                                                        >
                                                            {(u.nickname || u.firstName || 'م').charAt(0).toUpperCase()}
                                                        </div>
                                                        {u.isPremium && (
                                                            <div className="user-premium-badge-icon">
                                                                <Star size={10} fill="currentColor" color="#eab308" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="user-info-text">
                                                        <div className="user-display-name">
                                                            {u.firstName} {u.lastName && ` ${u.lastName}`}
                                                            {u.username && <span style={{ direction: 'ltr', fontSize: '0.8rem', color: '#64748b', fontWeight: 'normal', marginRight: '6px' }}>@{u.username}</span>}
                                                        </div>
                                                        <div style={{ marginTop: '2px', marginBottom: '2px' }}>
                                                            <span className="user-nickname-badge">
                                                                الاسم المستعار: {u.nickname || 'لم يحدد بعد'}
                                                            </span>
                                                        </div>
                                                        <div className="user-telegram-id-text">
                                                            ID: <span style={{ fontFamily: 'monospace' }}>{u.telegramId}</span>
                                                        </div>
                                                        <div className="user-badges-row">
                                                            {u.isVip && <span className="admin-badge vip">★ VIP عضوية</span>}
                                                            {u.isPremium && <span className="admin-badge telegram-premium">Telegram Premium</span>}
                                                            {u.isBanned && <span className="admin-badge banned">محظور 🚫</span>}
                                                            {settings.admins && settings.admins.map(id => id.toString()).includes(u.telegramId.toString()) && (
                                                                <span className="admin-badge staff-member">مشرف النظام</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="user-actions-row">
                                                    {u.isBanned ? (
                                                        <button 
                                                            className="action-btn-pill"
                                                            onClick={() => handleExecuteUserAction(u.telegramId, 'unban')}
                                                        >
                                                            إلغاء الحظر
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="action-btn-pill danger"
                                                            onClick={() => {
                                                                setBanTargetId(u.telegramId);
                                                                setBanType('user');
                                                                setBanDuration('permanent');
                                                                setBanModalOpen(true);
                                                            }}
                                                        >
                                                            حظر الحساب
                                                        </button>
                                                    )}

                                                    {u.isVip ? (
                                                        <button 
                                                            className="action-btn-pill"
                                                            onClick={() => handleExecuteUserAction(u.telegramId, 'demote_vip')}
                                                        >
                                                            إلغاء الـ VIP
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="action-btn-pill gold"
                                                            onClick={() => handleExecuteUserAction(u.telegramId, 'promote_vip')}
                                                        >
                                                            ترقية VIP (شهري)
                                                        </button>
                                                    )}

                                                    {settings.admins && settings.admins.map(id => id.toString()).includes(u.telegramId.toString()) ? (
                                                        <button 
                                                            className="action-btn-pill"
                                                            onClick={() => handleExecuteUserAction(u.telegramId, 'demote_admin')}
                                                        >
                                                            عزل مشرف
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="action-btn-pill purple"
                                                            onClick={() => handleExecuteUserAction(u.telegramId, 'promote_admin')}
                                                        >
                                                            تعيين مشرف
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'broadcast' && (
                        <div className="admin-card animate-fade">
                            <div className="card-header border-b mb-4">
                                <Megaphone className="header-icon" size={20} />
                                <h3>الإذاعة والنشر العام للبوت</h3>
                            </div>

                            <div className="broadcast-info-card mb-4">
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>📢 تنبيه هام للإدارة:</div>
                                ستصل هذه الرسالة الإذاعية لجميع مستخدمي البوت كرسالة مباشرة.
                                يرجى استخدام صياغة واضحة وHTML مدعوم فقط (مثل &lt;b&gt;رسالة غليظة&lt;/b&gt;، &lt;i&gt;رسالة مائلة&lt;/i&gt;).
                                تتم عملية النشر تدريجياً في الخلفية لحماية حساب البوت من تقييدات تيليجرام.
                            </div>

                            <div className="setting-item-block mb-4">
                                <label>نص الرسالة المراد نشرها</label>
                                <textarea
                                    className="admin-input"
                                    rows="6"
                                    value={broadcastText}
                                    placeholder="🎉 اكتب تفاصيل الإعلان أو الرسالة العامة هنا..."
                                    onChange={(e) => setBroadcastText(e.target.value)}
                                    style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                                />
                            </div>

                            <button 
                                className="btn-save-admin" 
                                onClick={handleSendBroadcast}
                                disabled={broadcasting || !broadcastText.trim()}
                                style={{ background: 'linear-gradient(135deg, #eab308, #ca8a04)' }}
                            >
                                <Megaphone size={20} />
                                {broadcasting ? 'جاري إرسال الإذاعة...' : 'إرسال الإذاعة العامة الآن'}
                            </button>
                        </div>
                    )}

                    {!isNativeMainActive && (activeTab === 'subscription' || activeTab === 'staff') && (
                        <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem', marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <ShieldCheck size={14} style={{ color: '#10b981' }} />
                            <span>تُحفظ جميع التغييرات تلقائياً</span>
                        </div>
                    )}
                </div>
            )}

            {/* إشعار الحفظ التلقائي المنبثق */}
            {toastMessage && (
                <div className="admin-auto-toast">
                    {toastMessage}
                </div>
            )}

            {/* نافذة تحديد مدة الحظر المؤقت والدائم الفخمة */}
            {banModalOpen && (
                <div className="ban-modal-overlay" onClick={() => setBanModalOpen(false)}>
                    <div className="ban-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ban-modal-title">
                            <AlertTriangle size={22} />
                            <span>تحديد مدة الحظر</span>
                        </div>
                        <div className="ban-modal-subtitle">
                            تطبيق الحظر على المعرّف: {banTargetId}
                        </div>
                        <div className="duration-options-list">
                            <div 
                                className={`duration-option-card ${banDuration === '24h' ? 'selected' : ''}`}
                                onClick={() => setBanDuration('24h')}
                            >
                                <span className="duration-label">24 ساعة</span>
                                <span className="duration-desc">يوم واحد فقط</span>
                            </div>
                            <div 
                                className={`duration-option-card ${banDuration === '3d' ? 'selected' : ''}`}
                                onClick={() => setBanDuration('3d')}
                            >
                                <span className="duration-label">3 أيام</span>
                                <span className="duration-desc">حظر مؤقت قصير</span>
                            </div>
                            <div 
                                className={`duration-option-card ${banDuration === '7d' ? 'selected' : ''}`}
                                onClick={() => setBanDuration('7d')}
                            >
                                <span className="duration-label">7 أيام</span>
                                <span className="duration-desc">أسبوع كامل</span>
                            </div>
                            <div 
                                className={`duration-option-card ${banDuration === '30d' ? 'selected' : ''}`}
                                onClick={() => setBanDuration('30d')}
                            >
                                <span className="duration-label">30 يوماً</span>
                                <span className="duration-desc">شهر كامل</span>
                            </div>
                            <div 
                                className={`duration-option-card ${banDuration === 'permanent' ? 'selected' : ''}`}
                                onClick={() => setBanDuration('permanent')}
                            >
                                <span className="duration-label" style={{ color: '#f43f5e' }}>حظر دائم</span>
                                <span className="duration-desc">منع نهائي من استخدام البوت</span>
                            </div>
                        </div>
                        <div className="ban-modal-actions">
                            <button className="btn-ban-submit" onClick={handleConfirmBan}>تأكيد الحظر</button>
                            <button className="btn-ban-cancel" onClick={() => setBanModalOpen(false)}>إلغاء</button>
                        </div>
                    </div>
                </div>
            )}

            {/* نافذة الرد على التذاكر والاقتراحات */}
            {replyModalOpen && replyTarget && (
                <div className="ban-modal-overlay" onClick={() => setReplyModalOpen(false)}>
                    <div className="ban-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
                        <div className="ban-modal-title">
                            <Send size={22} style={{ transform: 'rotate(180deg)' }} />
                            <span>الرد على رسالة المستخدم</span>
                        </div>
                        <div className="ban-modal-subtitle" style={{ textAlign: 'right', marginTop: '6px', fontSize: '0.85rem' }}>
                            <div style={{ color: '#64748b', marginBottom: '4px' }}>💬 نص الرسالة الأصلي:</div>
                            <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', color: '#fff', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                                {replyTarget.text}
                            </div>
                        </div>
                        <div className="setting-item-block mt-4">
                            <label>اكتب رد الإدارة الرسمي</label>
                            <textarea
                                className="admin-input"
                                rows="5"
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="اكتب ردك هنا... سيتم إرسال الرد كرسالة بوت تلقائية للمستخدم فور الحفظ ❤️"
                                style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                            />
                        </div>
                        <div className="ban-modal-actions">
                            <button 
                                className="btn-ban-submit" 
                                onClick={handleSendReply}
                                disabled={submittingReply || !replyText.trim()}
                                style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                            >
                                {submittingReply ? 'جاري إرسال الرد...' : 'إرسال الرد وحفظ'}
                            </button>
                            <button className="btn-ban-cancel" onClick={() => setReplyModalOpen(false)}>إلغاء</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
